import {
  CreateTask,
  ID,
  InsightsTimeframes,
  Task,
  TaskType,
} from '@griply/types';
import React, { useMemo, useState } from 'react';

import { MetaContentTabs } from './components/meta-content-tabs';
import { MetaSettings } from './components/meta-settings';
import { MetaStatisticsTimeframe } from './components/meta-statistics-timeframe';
import { Statistics } from './components/statistics';
import { Upcoming } from './components/upcoming';
import * as Styled from './meta.style';
import { MetaViewOptions } from './types';
import { useData } from './use-data';

export type TaskDetailMetaProps = {
  task: Task | CreateTask;
  updateTask: (task: Partial<Task>) => void;
  updateRelationships: (relationshipIds: ID[]) => void;
  view: MetaViewOptions;
  updateView: (view: MetaViewOptions) => void;
  isCreate?: boolean;
  onDelete?: () => void;
  onArchive?: () => void;
};

export const Meta: React.FC<TaskDetailMetaProps> = ({
  task,
  updateTask,
  updateRelationships,
  view,
  updateView,
  isCreate,
  onDelete,
  onArchive,
}) => {
  const { timeframe: initialTimeframe, updateHabitTimeframe } = useData();

  const [timeframe, setTimeframe] = useState(initialTimeframe);

  const updateTimeframe = (newTimeframe: InsightsTimeframes) => {
    setTimeframe(newTimeframe);
    updateHabitTimeframe?.(newTimeframe);
  };

  const content = useMemo(() => {
    if (
      view === MetaViewOptions.Statistics &&
      'id' in task &&
      task.type === TaskType.Habit
    ) {
      return <Statistics task={task} timeframe={timeframe} />;
    }

    if (
      view === MetaViewOptions.Upcoming &&
      'id' in task &&
      task.type !== TaskType.Task
    ) {
      return <Upcoming task={task} />;
    }

    return (
      <MetaSettings
        task={task}
        updateTask={updateTask}
        updateRelationships={updateRelationships}
        isCreate={isCreate}
        onDelete={onDelete}
        onArchive={onArchive}
      />
    );
  }, [
    view,
    task,
    updateTask,
    updateRelationships,
    isCreate,
    timeframe,
    onDelete,
    onArchive,
  ]);

  return (
    <Styled.Container>
      {'id' in task && task?.type !== TaskType.Task && (
        <Styled.Header>
          <MetaContentTabs
            onChange={updateView}
            selected={view}
            hideInsights={task.type !== TaskType.Habit}
          />
          {view === MetaViewOptions.Statistics && (
            <MetaStatisticsTimeframe
              timeframe={timeframe}
              onChange={updateTimeframe}
            />
          )}
        </Styled.Header>
      )}
      <Styled.Body>{content}</Styled.Body>
    </Styled.Container>
  );
};
